/**
 * DepartmentOverviewCalendar.js
 *
 * Read-only calendar that displays all shifts from every staff member
 * within a selected department. Supports both week and month view.
 * No editing, no drag interactions.
 *
 * Props:
 *   departmentStaff  - array of staff objects { id, name, ... }
 *   schedules        - { staffId: [...shifts] } from schedule state
 *   currentDate      - string YYYY-MM-DD
 *   viewMode         - 'week' | 'month'
 *   holidays         - array of { dateString, name, isPaidHoliday }
 *   dateRange        - { start: string, end: string }
 */

import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// ============================================================================
// CONSTANTS
// ============================================================================

const STAFF_COLORS = [
    { bg: 'rgba(59, 130, 246, 0.12)',  border: '#3b82f6', text: '#1d4ed8', dot: '#3b82f6' },
    { bg: 'rgba(16, 185, 129, 0.12)',  border: '#10b981', text: '#065f46', dot: '#10b981' },
    { bg: 'rgba(245, 158, 11, 0.12)',  border: '#f59e0b', text: '#78350f', dot: '#f59e0b' },
    { bg: 'rgba(239, 68, 68, 0.12)',   border: '#ef4444', text: '#991b1b', dot: '#ef4444' },
    { bg: 'rgba(139, 92, 246, 0.12)',  border: '#8b5cf6', text: '#5b21b6', dot: '#8b5cf6' },
    { bg: 'rgba(236, 72, 153, 0.12)',  border: '#ec4899', text: '#9d174d', dot: '#ec4899' },
    { bg: 'rgba(20, 184, 166, 0.12)',  border: '#14b8a6', text: '#134e4a', dot: '#14b8a6' },
    { bg: 'rgba(249, 115, 22, 0.12)',  border: '#f97316', text: '#9a3412', dot: '#f97316' },
];

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Returns the current window inner width and re-renders whenever the
 * browser is resized. Falls back to 1024 during SSR / test environments
 * where window is not defined.
 */
const useWindowWidth = () => {
    const [width, setWidth] = useState(
        typeof window !== 'undefined' ? window.innerWidth : 1024
    );

    useEffect(() => {
        const handleResize = () => setWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return width;
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Converts a Date object to a YYYY-MM-DD string using LOCAL time.
 * Never use toISOString() for date strings because it returns UTC,
 * which causes a one-day offset for timezones ahead of UTC (e.g. Germany).
 */
const toLocalDateString = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

/**
 * Converts any supported time format to total minutes since midnight.
 * Handles: Firestore Timestamp, "HH:MM" string, plain number.
 */
const convertTimeToMinutes = (time) => {
    if (!time) return 0;

    if (
        typeof time === 'object' &&
        time !== null &&
        typeof time.toDate === 'function'
    ) {
        const date = time.toDate();
        return date.getHours() * 60 + date.getMinutes();
    }

    if (typeof time === 'string') {
        const parts = time.split(':').map(Number);
        return (parts[0] || 0) * 60 + (parts[1] || 0);
    }

    if (typeof time === 'number') {
        return time;
    }

    return 0;
};

/**
 * Converts total minutes since midnight to "HH:MM" string.
 */
const convertMinutesToTime = (totalMinutes) => {
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
};

/**
 * Safely formats any time value (Timestamp, string, number) to "HH:MM".
 */
const formatTime = (time) => {
    if (!time) return '--:--';
    if (typeof time === 'string') return time;
    const minutes = convertTimeToMinutes(time);
    return convertMinutesToTime(minutes);
};

const formatDuration = (totalMinutes) => {
    if (!totalMinutes || totalMinutes <= 0) return '0 h';
    const hours   = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (minutes === 0) return `${hours} h`;
    return `${hours} h ${minutes} min`;
};

/**
 * Returns the duration of a shift in minutes regardless of how
 * startTime / endTime are stored in Firestore.
 */
const getShiftDuration = (shift) => {
    if (shift.totalMinutes && shift.totalMinutes > 0) return shift.totalMinutes;
    if (shift.startTime && shift.endTime) {
        const duration =
            convertTimeToMinutes(shift.endTime) -
            convertTimeToMinutes(shift.startTime);
        return Math.max(0, duration);
    }
    return 0;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const DepartmentOverviewCalendar = ({
    departmentStaff,
    schedules,
    currentDate,
    viewMode,
    holidays,
    dateRange
}) => {
    const { t, i18n } = useTranslation('schedule');

    // ========================================================================
    // RESPONSIVE BREAKPOINTS
    // isMobile drives padding, font size, and layout of the summary/legend
    // bars only. Grid column sizing is now handled entirely by CSS classes
    // (.doc-grid-7col) so media queries in schedule-planner-components.css
    // can take effect without inline style interference.
    // ========================================================================

    const windowWidth = useWindowWidth();
    const isMobile    = windowWidth < 480;

    // ========================================================================
    // HIGHLIGHT STATE
    // ========================================================================

    const [highlightedStaffId, setHighlightedStaffId] = useState(null);
    const legendRef = useRef(null);

    // Reset the highlight whenever the visible staff list changes
    useEffect(() => {
        setHighlightedStaffId(null);
    }, [departmentStaff]);

    // Deselect when the user clicks anywhere outside the legend chip bar
    useEffect(() => {
        if (!highlightedStaffId) return;

        const handleClickOutside = (e) => {
            if (legendRef.current && !legendRef.current.contains(e.target)) {
                setHighlightedStaffId(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [highlightedStaffId]);

    /**
     * Returns the opacity value a shift card or summary row should use.
     */
    const getShiftOpacity = (staffId) => {
        if (!highlightedStaffId) return 1;
        return staffId === highlightedStaffId ? 1 : 0.12;
    };

    // ========================================================================
    // DERIVED DATA
    // ========================================================================

    const today = useMemo(() => toLocalDateString(new Date()), []);

    const dayNamesShort = useMemo(() => [
        t('days.short.mon'),
        t('days.short.tue'),
        t('days.short.wed'),
        t('days.short.thu'),
        t('days.short.fri'),
        t('days.short.sat'),
        t('days.short.sun'),
    ], [t]);

    const staffColorMap = useMemo(() => {
        const map = {};
        departmentStaff.forEach((member, index) => {
            map[member.id] = STAFF_COLORS[index % STAFF_COLORS.length];
        });
        return map;
    }, [departmentStaff]);

    const holidaySet = useMemo(() => {
        const set = new Set();
        (holidays || []).forEach(h => {
            if (h.dateString) set.add(h.dateString);
        });
        return set;
    }, [holidays]);

    const weekDays = useMemo(() => {
        if (!dateRange.start) return [];
        const days  = [];
        const start = new Date(dateRange.start);
        for (let i = 0; i < 7; i++) {
            const date       = new Date(start);
            date.setDate(start.getDate() + i);
            const dateString = toLocalDateString(date);
            days.push({
                dateString,
                dayNum:    date.getDate(),
                monthNum:  date.getMonth() + 1,
                dayName:   dayNamesShort[i],
                isToday:   dateString === today,
                isHoliday: holidaySet.has(dateString)
            });
        }
        return days;
    }, [dateRange.start, holidaySet, dayNamesShort, today]);

    const monthGrid = useMemo(() => {
        const [year, month] = currentDate.split('-').map(Number);

        const firstDay = new Date(year, month - 1, 1);
        const lastDay  = new Date(year, month, 0);

        const gridStart = new Date(firstDay);
        const startDow  = gridStart.getDay();
        gridStart.setDate(gridStart.getDate() + (startDow === 0 ? -6 : 1 - startDow));

        const gridEnd  = new Date(lastDay);
        const endDow   = gridEnd.getDay();
        gridEnd.setDate(gridEnd.getDate() + (endDow === 0 ? 0 : 7 - endDow));

        const weeks  = [];
        const cursor = new Date(gridStart);

        while (cursor <= gridEnd) {
            const week = [];
            for (let i = 0; i < 7; i++) {
                const dateString = toLocalDateString(cursor);
                week.push({
                    dateString,
                    dayNum:         cursor.getDate(),
                    isCurrentMonth: cursor.getMonth() + 1 === month,
                    isToday:        dateString === today,
                    isHoliday:      holidaySet.has(dateString)
                });
                cursor.setDate(cursor.getDate() + 1);
            }
            weeks.push(week);
        }

        return { weeks, year, month };
    }, [currentDate, holidaySet, today]);

    const staffTotals = useMemo(() => {
        return departmentStaff.map(member => {
            const staffShifts = schedules[member.id] || [];
            let totalMinutes  = 0;

            if (viewMode === 'month') {
                const [year, month] = currentDate.split('-').map(Number);
                staffShifts.forEach(shift => {
                    const [sy, sm] = shift.date.split('-').map(Number);
                    if (sy === year && sm === month) {
                        totalMinutes += getShiftDuration(shift);
                    }
                });
            } else {
                if (dateRange.start && dateRange.end) {
                    staffShifts.forEach(shift => {
                        if (
                            shift.date >= dateRange.start &&
                            shift.date <= dateRange.end
                        ) {
                            totalMinutes += getShiftDuration(shift);
                        }
                    });
                }
            }

            return {
                id:           member.id,
                name:         member.name,
                totalMinutes,
                color:        staffColorMap[member.id]
            };
        });
    }, [departmentStaff, schedules, currentDate, viewMode, dateRange, staffColorMap]);

    const grandTotalMinutes = useMemo(
        () => staffTotals.reduce((sum, s) => sum + s.totalMinutes, 0),
        [staffTotals]
    );

    // ========================================================================
    // HELPER: collect and sort all shifts for a single date across all staff
    // ========================================================================

    const getShiftsForDate = (dateString) => {
        const result = [];
        departmentStaff.forEach(member => {
            (schedules[member.id] || [])
                .filter(shift => shift.date === dateString)
                .forEach(shift => {
                    result.push({
                        ...shift,
                        staffId:      member.id,
                        staffName:    member.name,
                        color:        staffColorMap[member.id],
                        startTimeStr: formatTime(shift.startTime),
                        endTimeStr:   formatTime(shift.endTime)
                    });
                });
        });
        return result.sort(
            (a, b) => convertTimeToMinutes(a.startTime) - convertTimeToMinutes(b.startTime)
        );
    };

    // ========================================================================
    // SHARED: summary bar
    // On mobile the Total row breaks onto its own line (flexBasis: '100%')
    // instead of being pushed right with marginLeft: 'auto'.
    // The coloured dot uses .doc-summary-dot so CSS can shrink it below 480px.
    // ========================================================================

    const renderSummaryBar = () => (
        <div style={{
            borderTop:       '2px solid #e5e7eb',
            padding:         isMobile ? '8px 12px' : '12px 16px',
            backgroundColor: '#f0f9ff',
            display:         'flex',
            flexWrap:        'wrap',
            gap:             isMobile ? '8px' : '12px',
            alignItems:      'center',
            flexShrink:      0
        }}>
            {staffTotals.map(staff => (
                <div key={staff.id} style={{
                    display:    'flex',
                    alignItems: 'center',
                    gap:        '6px',
                    fontSize:   isMobile ? '11px' : '12px',
                    opacity:    highlightedStaffId && staff.id !== highlightedStaffId
                        ? 0.35
                        : 1,
                    transition: 'opacity 0.2s ease'
                }}>
                    {/*
                        Dot — width, height, borderRadius, flexShrink moved to
                        .doc-summary-dot in schedule-planner-components.css so
                        the 480px breakpoint can shrink it without an inline override.
                    */}
                    <div
                        className="doc-summary-dot"
                        style={{ backgroundColor: staff.color.dot }}
                    />
                    <span style={{ color: '#374151', fontWeight: '500' }}>
                        {staff.name}:
                    </span>
                    <span style={{ color: staff.color.dot, fontWeight: '700' }}>
                        {formatDuration(staff.totalMinutes)}
                    </span>
                </div>
            ))}

            <div style={{
                marginLeft: isMobile ? '0' : 'auto',
                flexBasis:  isMobile ? '100%' : 'auto',
                display:    'flex',
                alignItems: 'center',
                gap:        '6px',
                fontSize:   isMobile ? '12px' : '13px'
            }}>
                <span style={{ color: '#6b7280', fontWeight: '400' }}>
                    {i18n.language === 'de' ? 'Gesamt' : 'Total'}:
                </span>
                <span style={{ color: '#1f2937', fontWeight: '700' }}>
                    {formatDuration(grandTotalMinutes)}
                </span>
            </div>
        </div>
    );

    // ========================================================================
    // SHARED: color legend with click-to-highlight
    // The coloured dot inside each chip uses .doc-legend-dot so CSS can
    // shrink it below 480px without an inline override blocking the rule.
    // ========================================================================

    const renderLegend = () => (
        <div
            ref={legendRef}
            style={{
                padding:         isMobile ? '6px 12px' : '8px 16px',
                borderBottom:    '1px solid #e5e7eb',
                backgroundColor: '#f9fafb',
                display:         'flex',
                flexWrap:        'wrap',
                gap:             isMobile ? '6px' : '10px',
                alignItems:      'center',
                flexShrink:      0
            }}
        >
            <span style={{ fontSize: '11px', color: '#9ca3af', marginRight: '4px' }}>
                {i18n.language === 'de' ? 'Mitarbeiter:' : 'Staff:'}
            </span>

            {departmentStaff.map((member, index) => {
                const color        = STAFF_COLORS[index % STAFF_COLORS.length];
                const isSelected   = highlightedStaffId === member.id;
                const someSelected = highlightedStaffId !== null;

                return (
                    <div
                        key={member.id}
                        onClick={() =>
                            setHighlightedStaffId(prev =>
                                prev === member.id ? null : member.id
                            )
                        }
                        style={{
                            display:         'flex',
                            alignItems:      'center',
                            gap:             '5px',
                            fontSize:        isMobile ? '10px' : '11px',
                            backgroundColor: color.bg,
                            border:          `1px solid ${color.border}`,
                            borderRadius:    '12px',
                            padding:         isMobile ? '2px 6px' : '2px 8px',
                            cursor:          'pointer',
                            userSelect:      'none',
                            transition:      'all 0.15s ease',
                            opacity:         someSelected && !isSelected ? 0.4 : 1,
                            boxShadow:       isSelected
                                ? `0 0 0 2px ${color.dot}`
                                : 'none'
                        }}
                    >
                        {/*
                            Dot — width, height, borderRadius, flexShrink moved to
                            .doc-legend-dot in schedule-planner-components.css.
                        */}
                        <div
                            className="doc-legend-dot"
                            style={{ backgroundColor: color.dot }}
                        />
                        <span style={{ color: color.text, fontWeight: '600' }}>
                            {member.name}
                        </span>
                    </div>
                );
            })}
        </div>
    );

    // ========================================================================
    // MONTH VIEW
    // The day-name header and all week rows now share a single
    // overflow: auto / minWidth: 560px wrapper so both axes scroll together.
    // Previously the header sat outside the scroll container, which caused
    // it to stay fixed while week rows scrolled independently on narrow
    // viewports.
    // ========================================================================

    if (viewMode === 'month') {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

                {renderLegend()}

                {/*
                    Single scrollable wrapper.
                    overflow: auto  — handles horizontal scroll on phones and
                                      vertical scroll for tall month grids.
                    minWidth: 560px — keeps each of the 7 columns at ~80px
                                      minimum; narrower viewports trigger the
                                      horizontal scrollbar instead of
                                      collapsing cells to an unreadable size.
                */}
                <div style={{ flex: 1, overflow: 'auto' }}>
                    <div style={{ minWidth: '560px' }}>

                        {/*
                            Day-name header row.
                            display + gridTemplateColumns moved to .doc-grid-7col
                            so the 768px / 480px breakpoints in CSS can override
                            column sizing without an inline style blocking them.
                        */}
                        <div
                            className="doc-grid-7col"
                            style={{
                                borderBottom:    '2px solid #e5e7eb',
                                paddingBottom:   '6px',
                                paddingTop:      '8px',
                                backgroundColor: 'white'
                            }}
                        >
                            {dayNamesShort.map(name => (
                                <div key={name} style={{
                                    textAlign:  'center',
                                    fontSize:   '12px',
                                    fontWeight: '600',
                                    color:      '#374151',
                                    padding:    '2px 4px'
                                }}>
                                    {name}
                                </div>
                            ))}
                        </div>

                        {/* Calendar weeks */}
                        {monthGrid.weeks.map((week, weekIndex) => (
                            /*
                                Week row.
                                display + gridTemplateColumns moved to .doc-grid-7col.
                            */
                            <div
                                key={weekIndex}
                                className="doc-grid-7col"
                                style={{
                                    borderBottom: '1px solid #e5e7eb',
                                    minHeight:    '90px'
                                }}
                            >
                                {week.map(day => {
                                    const shifts = getShiftsForDate(day.dateString);

                                    return (
                                        <div key={day.dateString} style={{
                                            padding:         '4px 5px',
                                            borderRight:     '1px solid #e5e7eb',
                                            backgroundColor: day.isHoliday
                                                ? 'rgba(139, 92, 246, 0.05)'
                                                : day.isCurrentMonth ? 'white' : '#fafafa',
                                            opacity:         day.isCurrentMonth ? 1 : 0.4,
                                            minHeight:       '90px'
                                        }}>
                                            {/*
                                                Day number circle.
                                                display, alignItems, justifyContent,
                                                width, height, borderRadius, fontSize,
                                                marginBottom moved to .doc-month-day-number
                                                in CSS so the 768px / 480px breakpoints
                                                can shrink the circle without an inline
                                                override blocking them.
                                                Dynamic fontWeight, color, backgroundColor
                                                remain inline because they depend on state.
                                            */}
                                            <div
                                                className="doc-month-day-number"
                                                style={{
                                                    fontWeight:      day.isToday ? '700' : '500',
                                                    color:           day.isToday
                                                        ? 'white'
                                                        : day.isHoliday ? '#7c3aed' : '#374151',
                                                    backgroundColor: day.isToday
                                                        ? '#1f2937'
                                                        : 'transparent'
                                                }}
                                            >
                                                {day.dayNum}
                                            </div>

                                            {/* Shift pills */}
                                            <div style={{
                                                display:       'flex',
                                                flexDirection: 'column',
                                                gap:           '2px'
                                            }}>
                                                {shifts.map(shift => (
                                                    <div
                                                        key={`${shift.staffId}-${shift.id || `${shift.date}-${shift.startTimeStr}`}`}
                                                        style={{
                                                            backgroundColor: shift.color.bg,
                                                            border:          `1px solid ${shift.color.border}`,
                                                            borderRadius:    '3px',
                                                            padding:         '1px 4px',
                                                            fontSize:        '10px',
                                                            lineHeight:      '1.4',
                                                            opacity:         getShiftOpacity(shift.staffId),
                                                            transition:      'opacity 0.2s ease'
                                                        }}
                                                    >
                                                        <div style={{
                                                            fontWeight:   '600',
                                                            color:        shift.color.text,
                                                            overflow:     'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace:   'nowrap'
                                                        }}>
                                                            {shift.staffName}
                                                        </div>
                                                        <div style={{ color: '#6b7280' }}>
                                                            {shift.startTimeStr}–{shift.endTimeStr}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ))}

                    </div>
                </div>

                {renderSummaryBar()}
            </div>
        );
    }

    // ========================================================================
    // WEEK VIEW
    // The day header row and shift-columns grid now share a single
    // overflow: auto / minWidth: 520px wrapper for the same reason as the
    // month view above. The previous approach placed the header outside the
    // scrollable div, so horizontal scrolling on phones moved only the shift
    // grid while the header stayed fixed and fell out of alignment.
    // ========================================================================

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

            {renderLegend()}

            {/*
                Single scrollable wrapper.
                overflow: auto  — horizontal scroll on phones; vertical scroll
                                  when shift content grows tall.
                minWidth: 520px — keeps each of the 7 columns at ~74px
                                  minimum on narrow viewports.
            */}
            <div style={{ flex: 1, overflow: 'auto' }}>
                <div style={{ minWidth: '520px' }}>

                    {/*
                        Day header row.
                        display + gridTemplateColumns moved to .doc-grid-7col
                        so CSS breakpoints can adjust column sizing.
                    */}
                    <div
                        className="doc-grid-7col"
                        style={{
                            borderBottom:    '2px solid #e5e7eb',
                            paddingTop:      '8px',
                            paddingBottom:   '6px',
                            backgroundColor: 'white'
                        }}
                    >
                        {weekDays.map(day => (
                            <div key={day.dateString} style={{
                                textAlign:       'center',
                                padding:         '4px',
                                backgroundColor: day.isHoliday
                                    ? 'rgba(139, 92, 246, 0.08)'
                                    : 'transparent',
                                borderRadius:    '6px'
                            }}>
                                <div style={{
                                    fontSize:   '12px',
                                    fontWeight: '600',
                                    color:      '#374151'
                                }}>
                                    {day.dayName}
                                </div>
                                {/*
                                    Date badge pill.
                                    display, alignItems, justifyContent, minWidth,
                                    height, borderRadius, fontSize, margin, padding
                                    moved to .doc-week-date-badge in CSS so the
                                    768px / 480px breakpoints can shrink the badge.
                                    Dynamic fontWeight, color, backgroundColor
                                    remain inline because they depend on state.
                                */}
                                <div
                                    className="doc-week-date-badge"
                                    style={{
                                        fontWeight:      day.isToday ? '700' : '400',
                                        color:           day.isToday ? 'white' : '#6b7280',
                                        backgroundColor: day.isToday
                                            ? '#1f2937'
                                            : 'transparent'
                                    }}
                                >
                                    {day.dayNum}.{day.monthNum.toString().padStart(2, '0')}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/*
                        Shift columns grid.
                        display + gridTemplateColumns moved to .doc-grid-7col;
                        align-items: start moved to .doc-week-shifts-grid.
                        Both classes are defined in schedule-planner-components.css.
                    */}
                    <div className="doc-grid-7col doc-week-shifts-grid">
                        {weekDays.map(day => {
                            const shifts = getShiftsForDate(day.dateString);

                            return (
                                <div key={day.dateString} style={{
                                    borderRight:     '1px solid #e5e7eb',
                                    padding:         '8px 6px',
                                    minHeight:       '140px',
                                    backgroundColor: day.isHoliday
                                        ? 'rgba(139, 92, 246, 0.03)'
                                        : 'white'
                                }}>
                                    {shifts.length === 0 ? (
                                        <div style={{
                                            textAlign:  'center',
                                            color:      '#d1d5db',
                                            fontSize:   '18px',
                                            paddingTop: '24px'
                                        }}>
                                            —
                                        </div>
                                    ) : (
                                        <div style={{
                                            display:       'flex',
                                            flexDirection: 'column',
                                            gap:           '6px'
                                        }}>
                                            {shifts.map(shift => (
                                                <div
                                                    key={`${shift.staffId}-${shift.id || `${shift.date}-${shift.startTimeStr}`}`}
                                                    style={{
                                                        backgroundColor: shift.color.bg,
                                                        border:          `1px solid ${shift.color.border}`,
                                                        borderLeft:      `3px solid ${shift.color.dot}`,
                                                        borderRadius:    '5px',
                                                        padding:         '5px 7px',
                                                        opacity:         getShiftOpacity(shift.staffId),
                                                        transition:      'opacity 0.2s ease'
                                                    }}
                                                >
                                                    <div style={{
                                                        fontSize:     '11px',
                                                        fontWeight:   '700',
                                                        color:        shift.color.text,
                                                        overflow:     'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace:   'nowrap',
                                                        marginBottom: '1px'
                                                    }}>
                                                        {shift.staffName}
                                                    </div>
                                                    <div style={{
                                                        fontSize:   '11px',
                                                        color:      '#374151',
                                                        fontWeight: '500'
                                                    }}>
                                                        {shift.startTimeStr} – {shift.endTimeStr}
                                                    </div>
                                                    <div style={{
                                                        fontSize:   '10px',
                                                        color:      shift.color.dot,
                                                        fontWeight: '600',
                                                        marginTop:  '1px'
                                                    }}>
                                                        {formatDuration(getShiftDuration(shift))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                </div>
            </div>

            {renderSummaryBar()}
        </div>
    );
};

export default DepartmentOverviewCalendar;